import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, UserPlus, Play, X, ChevronRight, Globe, Users } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WelcomeModal = ({ onCreateAccount, onContinueAnonymously, userId }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Welcome, 2: Age, 3: Region
  const [ageGroup, setAgeGroup] = useState('');
  const [region, setRegion] = useState('');

  useEffect(() => {
    // Check if user has seen welcome modal before
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
    
    // Don't show on admin routes or resources page (open access)
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    const isResourcesPage = window.location.pathname === '/resources';
    
    if (!hasSeenWelcome && !isAdminRoute && !isResourcesPage) {
      // Show modal after short delay
      setTimeout(() => setIsOpen(true), 500);
    }
  }, []);

  const handleCreateAccount = () => {
    setStep(2); // Go to age selection
  };

  const handleContinueAnonymously = () => {
    setStep(2); // Go to age selection for anonymous users too
  };

  const handleAgeSelection = (selectedAge) => {
    setAgeGroup(selectedAge);
    setStep(3); // Move to region selection
  };

  const handleRegionSelection = async (selectedRegion) => {
    setRegion(selectedRegion);
    
    // Save preferences to backend
    try {
      await axios.put(`${API}/profile/${userId}/preferences`, {
        age_group: ageGroup,
        region_preference: selectedRegion
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
    
    // Mark welcome as seen
    localStorage.setItem('hasSeenWelcome', 'true');
    setIsOpen(false);
    
    // Trigger the original action (create account or continue)
    if (step === 3) {
      // User came from either button, just close and let them continue
      window.location.reload(); // Reload to fetch filtered scenarios
    }
  };

  const handleClose = () => {
    localStorage.setItem('hasSeenWelcome', 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-ssdp-navy/40 backdrop-blur-md z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          key={step}
          initial={{ scale: 0.95, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 20, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="bg-white rounded-3xl p-8 sm:p-10 max-w-lg w-full relative shadow-ssdp-xl border border-ssdp-navy/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-ssdp-navy-light hover:text-ssdp-navy transition-colors rounded-full p-1 hover:bg-ssdp-teal/5"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Step 1: Welcome */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {/* Globe Icon with SSDP colors */}
              <div className="flex justify-center mb-6">
                <div className="relative w-24 h-24 bg-gradient-to-br from-ssdp-teal to-ssdp-purple rounded-full flex items-center justify-center shadow-ssdp-lg">
                  <Sparkles className="w-12 h-12 text-white" strokeWidth={2.5} />
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-ssdp-purple rounded-full border-4 border-white flex items-center justify-center">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-3xl sm:text-4xl font-bold text-center text-ssdp-navy mb-3 tracking-tight font-heading">
                Your Global Health Passport
              </h2>

              {/* Description */}
              <p className="text-center text-ssdp-navy-light mb-8 text-base leading-relaxed font-body max-w-md mx-auto">
                Join students worldwide fighting for drug policies rooted in human rights. Make safer, smarter decisions through interactive scenarios and real harm reduction education.
              </p>

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Create Account Button */}
                <Button
                  onClick={handleCreateAccount}
                  className="w-full bg-ssdp-teal text-white font-semibold py-4 rounded-full hover:bg-ssdp-teal-hover shadow-ssdp-sm hover:shadow-ssdp-md transition-all duration-200 text-base flex items-center justify-center gap-2 font-heading active:scale-[0.98]"
                  data-testid="welcome-create-account-btn"
                >
                  <UserPlus className="w-5 h-5" strokeWidth={2} />
                  Create Free Account
                  <ChevronRight className="w-5 h-5 ml-auto" />
                </Button>

                {/* Continue Anonymously Button */}
                <Button
                  onClick={handleContinueAnonymously}
                  variant="outline"
                  className="w-full bg-ssdp-purple/10 text-ssdp-navy border border-ssdp-purple/30 font-semibold py-4 rounded-full hover:bg-ssdp-purple/20 transition-all duration-200 text-base flex items-center justify-center gap-2 font-heading active:scale-[0.98]"
                  data-testid="welcome-continue-anonymous-btn"
                >
                  <Play className="w-5 h-5" strokeWidth={2} />
                  Continue Without Login
                  <ChevronRight className="w-5 h-5 ml-auto" />
                </Button>
              </div>

              {/* Benefits Note & SSDP Attribution */}
              <div className="mt-6 text-center space-y-2">
                <p className="text-xs text-ssdp-navy-light font-body">
                  💾 Create an account to save progress across devices
                </p>
                <p className="text-xs text-ssdp-navy-light/60 font-body">
                  By SSDP International
                </p>
              </div>
            </motion.div>
          )}

          {/* Step 2: Age Selection */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-ssdp-teal to-ssdp-purple rounded-full flex items-center justify-center">
                  <Users className="w-10 h-10 text-white" strokeWidth={2.5} />
                </div>
              </div>

              <h2 className="text-3xl font-bold text-center text-ssdp-navy mb-3 font-heading">
                What&apos;s your age group?
              </h2>

              <p className="text-center text-ssdp-navy-light mb-8 text-sm font-body">
                We&apos;ll recommend content that&apos;s most relevant to you
              </p>

              <div className="space-y-3">
                <Button
                  onClick={() => handleAgeSelection('13-15')}
                  className="w-full bg-white text-ssdp-navy border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-teal hover:text-white shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="age-13-15"
                >
                  13-15 years old
                </Button>
                <Button
                  onClick={() => handleAgeSelection('16-18')}
                  className="w-full bg-white text-ssdp-navy border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-teal hover:text-white shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="age-16-18"
                >
                  16-18 years old
                </Button>
                <Button
                  onClick={() => handleAgeSelection('19-24')}
                  className="w-full bg-white text-ssdp-navy border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-teal hover:text-white shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="age-19-24"
                >
                  19-24 years old
                </Button>
                <Button
                  onClick={() => handleAgeSelection('25-29')}
                  className="w-full bg-white text-ssdp-navy border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-teal hover:text-white shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="age-25-29"
                >
                  25-29 years old
                </Button>
                <Button
                  onClick={() => handleAgeSelection('30-35')}
                  className="w-full bg-white text-ssdp-navy border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-teal hover:text-white shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="age-30-35"
                >
                  30-35 years old
                </Button>
                <Button
                  onClick={() => {
                    // Redirect parents/guardians to SSDP website
                    window.location.href = 'https://www.ssdp-intl.org/';
                  }}
                  className="w-full bg-ssdp-purple text-white border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-purple/90 shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="age-parent"
                >
                  🏠 Parent/Guardian
                </Button>
              </div>

              <p className="text-xs text-center text-ssdp-navy-light/60 mt-6 font-body">
                You can change this later in your profile
              </p>
            </motion.div>
          )}

          {/* Step 3: Region Selection */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-ssdp-purple to-ssdp-teal rounded-full flex items-center justify-center">
                  <Globe className="w-10 h-10 text-white" strokeWidth={2.5} />
                </div>
              </div>

              <h2 className="text-3xl font-bold text-center text-ssdp-navy mb-3 font-heading">
                Where are you from?
              </h2>

              <p className="text-center text-ssdp-navy-light mb-8 text-sm font-body">
                Get scenarios tailored to your region
              </p>

              <div className="space-y-3">
                <Button
                  onClick={() => handleRegionSelection('south_africa')}
                  className="w-full bg-white text-ssdp-navy border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-purple hover:text-white shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="region-south-africa"
                >
                  🇿🇦 South Africa
                </Button>
                <Button
                  onClick={() => handleRegionSelection('global')}
                  className="w-full bg-white text-ssdp-navy border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-purple hover:text-white shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="region-global"
                >
                  🌍 Global / Other Country
                </Button>
                <Button
                  onClick={() => handleRegionSelection('both')}
                  className="w-full bg-white text-ssdp-navy border-3 border-ssdp-navy font-semibold py-4 rounded-2xl hover:bg-ssdp-purple hover:text-white shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[6px_6px_0px_0px_rgba(30,58,89,1)] transition-all text-base font-heading"
                  data-testid="region-both"
                >
                  ✨ Show Me Everything
                </Button>
              </div>

              <p className="text-xs text-center text-ssdp-navy-light/60 mt-6 font-body">
                Don&apos;t worry, you&apos;ll still see all content - just prioritized for you
              </p>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WelcomeModal;
